import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { clearDraftObject3d, deleteMapObject3d, selectDraftObject3d, setDraftObject3d, setMapObject3dEditMode, setSelectedMapObject3dId, updateDraftObject3d, updateMapObject3d } from '@repo/store/maps/slices/mapObjects3d';
import type { MapData3dObject, MapData3dObjectType } from '@repo/types/maps/map-3d-types';
import { EditMode } from '@repo/types/maps/map-3d-types';
import { ColorPicker } from '@repo/ui/components/color-picker/color-picker';
import { useEffect, useRef, useState } from 'react';
import type { EulerOrder } from 'three';
import { useObject3dUpload } from '../game-map/hooks/useObject3dUpload';
import { eulerToQuaternion, latLngToMeters, metersToLatLng, quaternionToEuler } from '../game-map/utils/object3dTransforms';
export const Object3dInfoEditing = ({
  model
}: {
  model: MapData3dObject;
}) => {
  const dispatch = useAppDispatch();
  const draftObject = useAppSelector(selectDraftObject3d);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    uploadModelFile,
    isUploadingModel
  } = useObject3dUpload();
  const [isUpdating, setIsUpdating] = useState(false);
  const activeModel = draftObject || model;
  const [title, setTitle] = useState(activeModel.properties.title || '');
  const [objectType, setObjectType] = useState<MapData3dObjectType>(activeModel.properties.type);
  const [color, setColor] = useState(activeModel.properties.color || '#ffffff');
  const [intensity, setIntensity] = useState<string>(activeModel.properties.intensity?.toString() || '1');
  const ORDER: EulerOrder = 'XYZ';
  const [minZoom, setMinZoom] = useState<string>(activeModel.properties.minZoom?.toString() || '');
  const [maxZoom, setMaxZoom] = useState<string>(activeModel.properties.maxZoom?.toString() || '');
  const euler = quaternionToEuler(activeModel.properties.quaternion || {
    x: 0,
    y: 0,
    z: 0,
    w: 1
  });
  const [rotationX, setRotationX] = useState(euler.x.toString());
  const [rotationY, setRotationY] = useState(euler.y.toString());
  const [rotationZ, setRotationZ] = useState(euler.z.toString());
  const [scaleX, setScaleX] = useState((activeModel.properties.scale?.x ?? 1).toString());
  const [scaleY, setScaleY] = useState((activeModel.properties.scale?.y ?? 1).toString());
  const [scaleZ, setScaleZ] = useState((activeModel.properties.scale?.z ?? 1).toString());
  const longitude = activeModel.geometry.coordinates[0] ?? 0;
  const latitude = activeModel.geometry.coordinates[1] ?? 0;
  const elevation = activeModel?.geometry?.coordinates?.[2] ?? 0;
  const [lat, setLat] = useState(latitude.toString());
  const [lng, setLng] = useState(longitude.toString());
  const [elev, setElev] = useState(elevation.toString());
  const meters = latLngToMeters(0, 0, parseFloat(lat), parseFloat(lng), parseFloat(elev));
  const [xMeters, setXMeters] = useState(meters.xMeters.toString());
  const [yMeters, setYMeters] = useState(meters.yMeters.toString());
  const [zMeters, setZMeters] = useState(meters.zMeters.toString());

  // Initialize draft when model changes or component mounts
  useEffect(() => {
    if (!draftObject || draftObject.properties.externalId !== model.properties.externalId) {
      dispatch(setDraftObject3d(model));
    }
  }, [model.properties.externalId, dispatch, draftObject, model]);

  // Sync local state with active model
  useEffect(() => {
    setTitle(activeModel.properties.title || '');
    setObjectType(activeModel.properties.type);
    setColor(activeModel.properties.color || '#ffffff');
    setIntensity(activeModel.properties.intensity?.toString() || '1');
    const euler_0 = quaternionToEuler(activeModel.properties.quaternion || {
      x: 0,
      y: 0,
      z: 0,
      w: 1
    });
    setRotationX(euler_0.x.toString());
    setRotationY(euler_0.y.toString());
    setRotationZ(euler_0.z.toString());
    setScaleX((activeModel.properties.scale?.x ?? 1).toString());
    setScaleY((activeModel.properties.scale?.y ?? 1).toString());
    setScaleZ((activeModel.properties.scale?.z ?? 1).toString());
    const longitude_0 = activeModel.geometry.coordinates[0] ?? 0;
    const latitude_0 = activeModel.geometry.coordinates[1] ?? 0;
    const elevation_0 = activeModel?.geometry?.coordinates?.[2] ?? 0;
    setLat(latitude_0.toString());
    setLng(longitude_0.toString());
    setElev(elevation_0.toString());
    const meters_0 = latLngToMeters(0, 0, latitude_0, longitude_0, elevation_0);
    setXMeters(meters_0.xMeters.toString());
    setYMeters(meters_0.yMeters.toString());
    setZMeters(meters_0.zMeters.toString());
    setMinZoom(activeModel.properties.minZoom?.toString() || '');
    setMaxZoom(activeModel.properties.maxZoom?.toString() || '');
  }, [activeModel]);
  const updateDraftRotation = (x: string, y: string, z: string) => {
    const quaternion = eulerToQuaternion(parseFloat(x), parseFloat(y), parseFloat(z), ORDER);
    dispatch(updateDraftObject3d({
      properties: {
        quaternion
      }
    }));
  };
  const updateDraftScale = (x_0: string, y_0: string, z_0: string) => {
    dispatch(updateDraftObject3d({
      properties: {
        scale: {
          x: parseFloat(x_0),
          y: parseFloat(y_0),
          z: parseFloat(z_0)
        }
      }
    }));
  };
  const updateDraftPositionFromLatLng = (latitude_1: string, longitude_1: string, elevation_1: string) => {
    const lat_0 = parseFloat(latitude_1);
    const lng_0 = parseFloat(longitude_1);
    const elev_0 = parseFloat(elevation_1);
    const meters_1 = latLngToMeters(0, 0, lat_0, lng_0, elev_0);
    setXMeters(meters_1.xMeters.toString());
    setYMeters(meters_1.yMeters.toString());
    setZMeters(meters_1.zMeters.toString());
    dispatch(updateDraftObject3d({
      geometry: {
        coordinates: [lng_0, lat_0, elev_0] as [number, number, number]
      }
    }));
  };
  const updateDraftPositionFromMeters = (x_1: string, y_1: string, z_1: string) => {
    const {
      lng: lng_1,
      lat: lat_1,
      elevation: elevation_2
    } = metersToLatLng(parseFloat(x_1), parseFloat(y_1), parseFloat(z_1), 0, 0);
    setLat(lat_1.toString());
    setLng(lng_1.toString());
    setElev(elevation_2.toString());
    dispatch(updateDraftObject3d({
      geometry: {
        coordinates: [lng_1, lat_1, elevation_2] as [number, number, number]
      }
    }));
  };
  const handleSave = async () => {
    if (!draftObject) return;
    setIsUpdating(true);
    try {
      await dispatch(updateMapObject3d({
        objectId: draftObject.properties.externalId,
        properties: {
          title,
          type: objectType,
          scale: draftObject.properties.scale,
          quaternion: draftObject.properties.quaternion,
          minZoom: minZoom ? parseFloat(minZoom) : undefined,
          maxZoom: maxZoom ? parseFloat(maxZoom) : undefined,
          modelPath: draftObject.properties.modelPath,
          color: color,
          intensity: intensity ? parseFloat(intensity) : undefined
        },
        geometry: draftObject.geometry
      })).unwrap();
      toast({
        title: '3D Object updated',
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: 'Failed to update 3D object',
        description: error instanceof Error ? error.message : JSON.stringify(error),
        variant: 'error'
      });
    } finally {
      setIsUpdating(false);
    }
  };
  const handleCancel = () => {
    dispatch(clearDraftObject3d());
    dispatch(setDraftObject3d(model));
  };
  const handleDelete = async () => {
    if (confirm(`Delete 3D object "${model.properties.title || 'Unnamed'}"?`)) {
      try {
        await dispatch(deleteMapObject3d({
          objectId: model.properties.externalId
        })).unwrap();
        dispatch(setSelectedMapObject3dId(null));
        toast({
          title: '3D Object deleted',
          variant: 'success'
        });
      } catch (error_0) {
        toast({
          title: 'Failed to delete 3D object',
          description: error_0 instanceof Error ? error_0.message : JSON.stringify(error_0),
          variant: 'error'
        });
      }
    }
  };
  const handleModelFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['.glb', '.gltf'];
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (!validExtensions.includes(fileExtension)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a .glb or .gltf file',
          variant: 'error'
        });
        return;
      }
      try {
        await uploadModelFile(model.properties.externalId, file, model);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error_1) {
        console.error('Model upload error:', error_1);
      }
    }
  };
  const handleClose = () => {
    dispatch(clearDraftObject3d());
    dispatch(setMapObject3dEditMode(null));
    dispatch(setSelectedMapObject3dId(null));
  };
  const handleMoveModel = () => {
    dispatch(setMapObject3dEditMode(EditMode.Translate));
  };
  const handleScaleModel = () => {
    dispatch(setMapObject3dEditMode(EditMode.Scale));
  };
  const handleRotateModel = () => {
    dispatch(setMapObject3dEditMode(EditMode.Rotate));
  };
  return <>
            <FlexRow align="center" gap="8" width="full" className={css.raw({
      padding: '4',
      paddingBottom: '12'
    })}>
                <Type variant="header-s" color="white">
                    Edit 3D Object
                </Type>
                <DropdownMenu trigger={<IconButton color="secondary" size="sm" aria-label="More options" className={css({
        marginLeft: 'auto'
      })}>
                            <Icon icon="more_vert" decorative />
                        </IconButton>}>
                    <DropdownMenuItem value="move" leadingIcon="open_with" onSelect={handleMoveModel}>
                        Move Model
                    </DropdownMenuItem>
                    <DropdownMenuItem value="scale" leadingIcon="photo_size_select_small" onSelect={handleScaleModel}>
                        Scale Model
                    </DropdownMenuItem>
                    <DropdownMenuItem value="rotate" leadingIcon="360" onSelect={handleRotateModel}>
                        Rotate Model
                    </DropdownMenuItem>
                    <DropdownMenuItem value="delete" leadingIcon="delete" onSelect={handleDelete} destructive>
                        Delete Model
                    </DropdownMenuItem>
                </DropdownMenu>
                <IconButton onClick={handleClose} color="secondary" size="sm" aria-label="Close">
                    <Icon icon="close" decorative />
                </IconButton>
            </FlexRow>

            <ScrollableContent maxHeight={600} className={css({
      overflow: 'hidden',
      display: 'flex',
      flex: '1'
    })}>
                <FlexColumn gap="12" width="full" className={css.raw({
        padding: '8'
      })}>
                    <TextInput ref={titleInputRef} value={title} onChange={e_0 => setTitle(e_0.target.value)} placeholder="Model Title" label="Title" size="sm" fullWidth />

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Object Type
                        </Type>
                        <Select value={objectType} onValueChange={value => setObjectType(value as 'model' | 'directional_light' | 'hemisphere_light' | 'ambient_light' | 'point_light' | 'spot_light')} size="sm" fullWidth trigger={<Button color="primary" size="sm">
                                    {objectType === 'model' && 'Model'}
                                    {objectType === 'directional_light' && 'Directional Light'}
                                    {objectType === 'hemisphere_light' && 'Hemisphere Light'}
                                    {objectType === 'ambient_light' && 'Ambient Light'}
                                    {objectType === 'point_light' && 'Point Light'}
                                    {objectType === 'spot_light' && 'Spot Light'}
                                </Button>}>
                            <SelectItem item={{
              label: 'Model',
              value: 'model'
            }}>Model</SelectItem>
                            <SelectItem item={{
              label: 'Directional Light',
              value: 'directional_light'
            }}>
                                Directional Light
                            </SelectItem>
                            <SelectItem item={{
              label: 'Hemisphere Light',
              value: 'hemisphere_light'
            }}>
                                Hemisphere Light
                            </SelectItem>
                            <SelectItem item={{
              label: 'Ambient Light',
              value: 'ambient_light'
            }}>
                                Ambient Light
                            </SelectItem>
                            <SelectItem item={{
              label: 'Point Light',
              value: 'point_light'
            }}>Point Light</SelectItem>
                            <SelectItem item={{
              label: 'Spot Light',
              value: 'spot_light'
            }}>Spot Light</SelectItem>
                        </Select>
                    </FlexColumn>

                    {objectType !== 'model' && <>
                            <ColorPicker label="Light Color" value={color} onChange={setColor} size="sm" fullWidth />

                            <TextInput value={intensity} onChange={e_1 => setIntensity(e_1.target.value)} placeholder="Light Intensity" label="Intensity" type="number" step="0.1" min="0" size="sm" fullWidth />
                        </>}

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Position (Lat/Lng/Elevation)
                        </Type>
                        <FlexRow gap="8" width="full">
                            <TextInput value={lat} onChange={e_2 => {
              setLat(e_2.target.value);
              updateDraftPositionFromLatLng(e_2.target.value, lng, elev);
            }} placeholder="Latitude" label="Lat" type="number" step="0.000001" size="sm" fullWidth />
                            <TextInput value={lng} onChange={e_3 => {
              setLng(e_3.target.value);
              updateDraftPositionFromLatLng(lat, e_3.target.value, elev);
            }} placeholder="Longitude" label="Lng" type="number" step="0.000001" size="sm" fullWidth />
                            <TextInput value={elev} onChange={e_4 => {
              setElev(e_4.target.value);
              updateDraftPositionFromLatLng(lat, lng, e_4.target.value);
            }} placeholder="Elevation" label="Elev" type="number" step="0.1" size="sm" fullWidth />
                        </FlexRow>
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Position (XYZ Meters)
                        </Type>
                        <FlexRow gap="8" width="full">
                            <TextInput value={xMeters} onChange={e_5 => {
              setXMeters(e_5.target.value);
              updateDraftPositionFromMeters(e_5.target.value, yMeters, zMeters);
            }} placeholder="X" label="X (m)" type="number" step="0.1" size="sm" fullWidth />
                            <TextInput value={yMeters} onChange={e_6 => {
              setYMeters(e_6.target.value);
              updateDraftPositionFromMeters(xMeters, e_6.target.value, zMeters);
            }} placeholder="Y" label="Y (m)" type="number" step="0.1" size="sm" fullWidth />
                            <TextInput value={zMeters} onChange={e_7 => {
              setZMeters(e_7.target.value);
              updateDraftPositionFromMeters(xMeters, yMeters, e_7.target.value);
            }} placeholder="Z" label="Z (m)" type="number" step="0.1" size="sm" fullWidth />
                        </FlexRow>
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Scale
                        </Type>
                        <FlexRow gap="8" width="full">
                            <TextInput value={scaleX} onChange={e_8 => {
              setScaleX(e_8.target.value);
              updateDraftScale(e_8.target.value, scaleY, scaleZ);
            }} placeholder="X" label="X" type="number" step="0.1" size="sm" fullWidth />
                            <TextInput value={scaleY} onChange={e_9 => {
              setScaleY(e_9.target.value);
              updateDraftScale(scaleX, e_9.target.value, scaleZ);
            }} placeholder="Y" label="Y" type="number" step="0.1" size="sm" fullWidth />
                            <TextInput value={scaleZ} onChange={e_10 => {
              setScaleZ(e_10.target.value);
              updateDraftScale(scaleX, scaleY, e_10.target.value);
            }} placeholder="Z" label="Z" type="number" step="0.1" size="sm" fullWidth />
                        </FlexRow>
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Rotation (degrees)
                        </Type>
                        <FlexRow gap="8" width="full">
                            <TextInput value={rotationX} onChange={e_11 => {
              setRotationX(e_11.target.value);
              updateDraftRotation(e_11.target.value, rotationY, rotationZ);
            }} placeholder="X" label="Pitch (X)" type="number" step="1" size="sm" fullWidth />
                            <TextInput value={rotationY} onChange={e_12 => {
              setRotationY(e_12.target.value);
              updateDraftRotation(rotationX, e_12.target.value, rotationZ);
            }} placeholder="Y" label="Yaw (Y)" type="number" step="1" size="sm" fullWidth />
                            <TextInput value={rotationZ} onChange={e_13 => {
              setRotationZ(e_13.target.value);
              updateDraftRotation(rotationX, rotationY, e_13.target.value);
            }} placeholder="Z" label="Roll (Z)" type="number" step="1" size="sm" fullWidth />
                        </FlexRow>
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Model File
                        </Type>
                        {model.properties.modelPath && <FlexRow gap="8" align="center">
                                <Type variant="body-s">Current: {model.properties.modelPath.split('/').pop()}</Type>
                            </FlexRow>}
                        <Button onClick={() => fileInputRef.current?.click()} variant="filled" color="secondary" size="sm" fullWidth disabled={isUploadingModel}>
                            <Icon icon="upload" decorative />
                            {isUploadingModel ? 'Uploading...' : 'Upload GLB/GLTF'}
                        </Button>
                        <input ref={fileInputRef} type="file" accept=".glb,.gltf" onChange={handleModelFileChange} style={{
            display: 'none'
          }} />
                    </FlexColumn>

                    <FlexRow gap="8" width="full">
                        <TextInput value={minZoom} onChange={e_14 => setMinZoom(e_14.target.value)} placeholder="Min Zoom" label="Min Zoom" type="number" size="sm" fullWidth />
                        <TextInput value={maxZoom} onChange={e_15 => setMaxZoom(e_15.target.value)} placeholder="Max Zoom" label="Max Zoom" type="number" size="sm" fullWidth />
                    </FlexRow>
                </FlexColumn>
            </ScrollableContent>

            <FlexColumn gap="8" justify="end" width="full">
                <Button onClick={handleSave} color="success" size="sm" fullWidth disabled={isUpdating}>
                    {isUpdating ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="ghost" onClick={handleCancel} color="primary" size="sm" fullWidth disabled={isUpdating}>
                    Reset
                </Button>
                <Button variant="ghost" onClick={handleClose} color="primary" size="sm" fullWidth>
                    Close
                </Button>
            </FlexColumn>
        </>;
};